import RefreshIcon from "@mui/icons-material/Refresh";
import SendIcon from "@mui/icons-material/Send";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { useMemo } from "react";

const pick = (obj, camel, pascal) => obj?.[camel] ?? obj?.[pascal] ?? "";

const isRejectedStatus = (code, title) => {
  const normalized = String(code || "").trim().toLowerCase();
  if (normalized.includes("reject")) return true;
  const text = String(title || "");
  return text.includes("رد") || text.includes("ناموفق");
};

const InfoField = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600}>
      {value || "—"}
    </Typography>
  </Box>
);

InfoField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const IriaPortalDetailPanel = ({
  externalApplicationId,
  statusCode,
  statusTitle,
  portalMessages,
  infoFields,
  loading,
  onRefresh,
  showSubmit = false,
  onSubmit,
  submitLabel = "ارسال به ایریاپی",
  compact = false,
  title = "وضعیت در پنل ایریاپی",
  refreshLabel = "بروزرسانی جزئیات",
}) => {
  const rejected = useMemo(
    () => isRejectedStatus(statusCode, statusTitle),
    [statusCode, statusTitle]
  );

  const infoEntries = useMemo(() => {
    if (!infoFields || typeof infoFields !== "object") return [];
    return Object.entries(infoFields).filter(
      ([, value]) => value != null && String(value).trim() !== "" && value !== "-"
    );
  }, [infoFields]);

  const hasContent =
    statusTitle ||
    statusCode ||
    portalMessages ||
    infoEntries.length > 0 ||
    externalApplicationId;

  if (!hasContent && !onRefresh && !showSubmit) return null;

  return (
    <Paper variant="outlined" sx={{ p: compact ? 1.5 : 2, borderRadius: 2 }}>
      <Stack spacing={1.5}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={1}
        >
          <Typography variant="subtitle2" fontWeight={700}>
            {title}
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {showSubmit && onSubmit ? (
              <Button
                size="small"
                variant="contained"
                color="secondary"
                startIcon={
                  loading ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <SendIcon fontSize="small" />
                  )
                }
                disabled={loading}
                onClick={onSubmit}
              >
                {loading ? "در حال ارسال..." : submitLabel}
              </Button>
            ) : null}
            {onRefresh ? (
              <Button
                size="small"
                variant="outlined"
                startIcon={
                  loading ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />
                }
                disabled={loading}
                onClick={onRefresh}
              >
                {loading ? "در حال بروزرسانی..." : refreshLabel}
              </Button>
            ) : null}
          </Stack>
        </Stack>

        {!hasContent && showSubmit ? (
          <Alert severity="warning" variant="outlined" sx={{ py: 0.75 }}>
            شناسه ایریاپی یافت نشد. برای ثبت متقاضی در پنل، «ارسال به ایریاپی» را بزنید.
          </Alert>
        ) : null}

        <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
          {statusTitle ? (
            <Chip
              size="small"
              color={rejected ? "error" : "info"}
              label={statusTitle}
            />
          ) : null}
          {externalApplicationId ? (
            <Chip
              size="small"
              variant="outlined"
              label={`شناسه ایریا: ${externalApplicationId}`}
            />
          ) : null}
          {statusCode && statusCode !== statusTitle ? (
            <Typography variant="caption" color="text.secondary">
              ({statusCode})
            </Typography>
          ) : null}
        </Stack>

        {infoEntries.length > 0 ? (
          <Grid container spacing={1.5}>
            {infoEntries.map(([label, value]) => (
              <Grid item xs={12} sm={6} md={4} key={label}>
                <InfoField label={label} value={value} />
              </Grid>
            ))}
          </Grid>
        ) : null}

        {portalMessages ? (
          <Alert severity={rejected ? "error" : "info"} variant="outlined" sx={{ py: 0.75 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
              {rejected ? "دلیل رد / پیام‌های ایریاپی" : "پیام‌ها و دلایل ایریاپی"}
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
              {portalMessages}
            </Typography>
          </Alert>
        ) : null}
      </Stack>
    </Paper>
  );
};

IriaPortalDetailPanel.propTypes = {
  externalApplicationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  statusCode: PropTypes.string,
  statusTitle: PropTypes.string,
  portalMessages: PropTypes.string,
  infoFields: PropTypes.object,
  loading: PropTypes.bool,
  onRefresh: PropTypes.func,
  showSubmit: PropTypes.bool,
  onSubmit: PropTypes.func,
  submitLabel: PropTypes.string,
  compact: PropTypes.bool,
  title: PropTypes.string,
  refreshLabel: PropTypes.string,
};

export const pickIriaPortalFields = (row, loanProcess, detailResult) => ({
  externalApplicationId:
    pick(detailResult, "externalApplicationId", "ExternalApplicationId") ||
    pick(row, "trackingCode", "TrackingCode") ||
    pick(loanProcess, "trackingCode", "TrackingCode") ||
    "",
  statusCode:
    pick(detailResult, "statusCode", "StatusCode") ||
    pick(row, "partnerExternalStatusCode", "PartnerExternalStatusCode") ||
    pick(loanProcess, "partnerExternalStatusCode", "PartnerExternalStatusCode") ||
    "",
  statusTitle:
    pick(detailResult, "statusTitle", "StatusTitle") ||
    pick(row, "partnerExternalStatusTitle", "PartnerExternalStatusTitle") ||
    pick(loanProcess, "partnerExternalStatusTitle", "PartnerExternalStatusTitle") ||
    "",
  portalMessages:
    pick(detailResult, "portalMessages", "PortalMessages") ||
    pick(row, "partnerRejectionReason", "PartnerRejectionReason") ||
    pick(loanProcess, "partnerRejectionReason", "PartnerRejectionReason") ||
    "",
  infoFields:
    detailResult?.infoFields ||
    detailResult?.InfoFields ||
    null,
});

export default IriaPortalDetailPanel;
